import React, { Fragment } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { CelebrationPersona, EnrichedCelebration } from './celebration.types';
import {
  CELEBRATION_OVERLAY,
  CELEBRATION_PANEL,
  CELEBRATION_PRIMARY_CTA,
  CELEBRATION_SECONDARY_CTA,
} from './celebration.tokens';

type Props = {
  open: boolean;
  items: EnrichedCelebration[];
  persona: CelebrationPersona;
  progressHref: string;
  testId?: string;
  onClose: () => void;
};

/** Multiple Tier 3 unlocks — one summary, route to progress hub. */
export const CelebrationBatchModal: React.FC<Props> = ({
  open,
  items,
  persona,
  progressHref,
  testId = 'celebration-batch-modal',
  onClose,
}) => {
  if (items.length === 0) return null;

  return (
    <Transition show={open} as={Fragment}>
      <Dialog className="relative z-[200]" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-250"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={`fixed inset-0 ${CELEBRATION_OVERLAY} pointer-events-none`}
            aria-hidden
          />
        </TransitionChild>

        <div className="pointer-events-none fixed inset-0 z-[201] flex items-center justify-center p-4">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-250"
            enterFrom="opacity-0 scale-[0.98]"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-[0.98]"
          >
            <DialogPanel
              className={`pointer-events-auto w-full max-w-md ${CELEBRATION_PANEL[persona]} p-6`}
              data-testid={testId}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {items.length} major milestones
                  </p>
                  <h2 className="mt-1 text-lg font-semibold text-slate-900">Wins waiting for you</h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Review each celebration on your progress page — we saved them so nothing blocks
                    your work.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"
                  aria-label="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <ul className="mt-4 max-h-48 space-y-2 overflow-y-auto">
                {items.map((item) => (
                  <li
                    key={item.id}
                    className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2 text-sm text-slate-800"
                  >
                    {item.title}
                  </li>
                ))}
              </ul>

              <div className="mt-5 flex flex-col gap-2">
                <Link to={progressHref} onClick={onClose} className={CELEBRATION_PRIMARY_CTA[persona]}>
                  Review on progress page
                </Link>
                <button type="button" onClick={onClose} className={CELEBRATION_SECONDARY_CTA[persona]}>
                  Continue working
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};
